import { useQuery, useQueries } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/polygon/api/config.ts";
import { useOrganizationStore } from "@/shared/stores/useOrganizationStore";
import { getAllPolygonsWithCoordinates, getPolygonByIdWithCoordinates } from "../requests/polygon.requests";
import { Polygon } from "../../types/polygon";

const STALE_TIME = 1000 * 60 * 60 * 12; // 12 hours - data considered fresh
const GC_TIME = 1000 * 60 * 60 * 24; // 24 hours - keep in cache

export const useGetAllPolygonsForSelector = (locationId?: number) => {
  const orgId = useOrganizationStore((s) => s.selectedOrganizationId);

  return useQuery<Polygon[]>({
    queryKey: [QUERY_KEYS.POLYGON_SELECTOR, "list", orgId, locationId ?? null],
    queryFn: async () => {
      const response = await getAllPolygonsWithCoordinates(locationId);
      return response.data || [];
    },
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
};

export const useGetPolygonByIdForSelector = (id: number | null) => {
  const orgId = useOrganizationStore((s) => s.selectedOrganizationId);

  return useQuery<Polygon>({
    queryKey: [QUERY_KEYS.POLYGON_SELECTOR, "detail", orgId, id],
    queryFn: async () => {
      if (!id) throw new Error("Polygon ID is required");
      const response = await getPolygonByIdWithCoordinates(id);
      return response.data;
    },
    enabled: !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
};

export const useGetPolygonsByIdsForSelector = (ids: number[]) => {
  const orgId = useOrganizationStore((s) => s.selectedOrganizationId);

  const queries = useQueries({
    queries: ids.map((id) => ({
      queryKey: [QUERY_KEYS.POLYGON_SELECTOR, "detail", orgId, id],
      queryFn: async () => {
        const response = await getPolygonByIdWithCoordinates(id);
        return response.data;
      },
      staleTime: STALE_TIME,
      gcTime: GC_TIME,
    })),
  });

  const polygons = queries
    .filter((q) => q.isSuccess && q.data)
    .map((q) => q.data);

  const isLoading = queries.some((q) => q.isLoading);

  return { polygons, isLoading };
};
